import { getDefaultPageSize } from '@helpers/cookie'
import { getCursorFromUrl } from '@helpers/url'

import authActions from '../auth/actions'
import filtersActions from '../filters/actions'
import actions from './actions'
import type { PaymentReducerAction } from './types'

const defaultPageSize = getDefaultPageSize()

export const initialState: PaymentStore = {
  bankTransactions: {
    cursor: null,
    data: [],
    error: null,
    fetched: false,
    loading: false,
    nextPageUrl: null,
    pageSize: defaultPageSize,
    prevPageUrl: null,
  },
  paidThroughs: {
    cursor: null,
    data: [],
    error: null,
    fetched: false,
    loading: false,
    nextPageUrl: null,
    pageSize: defaultPageSize,
    prevPageUrl: null,
  },
  paidThroughOptions: [],
  paymentTransactions: {
    cursor: null,
    data: [],
    error: null,
    fetched: false,
    loading: false,
    nextPageUrl: null,
    pageSize: defaultPageSize,
    prevPageUrl: null,
  },
}

function reducer(state: PaymentStore = initialState, action: PaymentReducerAction): PaymentStore {
  switch (action.type) {
    //* reset data when change company
    case authActions.selectCompany.REQUEST:
      return { ...initialState }

    //* PAID THROUGH
    case filtersActions.initPaidThroughListParamsFromUrl.REQUEST: {
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          ...action.payload,
        },
      }
    }
    case actions.fetchPaidThroughs.REQUEST:
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          loading: true,
          fetched: false,
        },
      }

    case filtersActions.updatePaidThroughFilters.REQUEST:
    case filtersActions.resetPaidThroughFilters.REQUEST:
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          loading: true,
        },
      }

    case actions.fetchPaidThroughs.SUCCESS:
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          data: action.payload.results,
          error: null,
          fetched: true,
          loading: false,
          nextPageUrl: action.payload.next,
          prevPageUrl: action.payload.previous,
        },
      }

    case actions.fetchPaidThroughs.FAILURE:
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          error: action.payload,
          loading: false,
        },
      }

    case actions.fetchPaidThroughOptions.SUCCESS:
      return {
        ...state,
        paidThroughOptions: action.payload,
      }

    // pagination
    case actions.fetchPaidThroughsByPaging.REQUEST: {
      const cursor = getCursorFromUrl(action.payload.url)
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          cursor,
          fetched: false,
          loading: true,
        },
      }
    }

    case actions.updatePaidThroughsRowsPerPage.REQUEST:
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          cursor: null,
          loading: true,
          pageSize: action.payload.pageSize,
        },
      }

    case actions.resetPaidThroughsPagination.REQUEST: {
      return {
        ...state,
        paidThroughs: {
          ...state.paidThroughs,
          cursor: null,
        },
      }
    }

    //* BANK TRANSACTIONS
    case filtersActions.initBankTransactionListParamsFromUrl.REQUEST: {
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          ...action.payload,
        },
      }
    }

    case actions.fetchBankTransactions.REQUEST:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          fetched: false,
          loading: true,
        },
      }

    case filtersActions.updateBankTransactionsFilters.REQUEST:
    case filtersActions.resetBankTransactionsFilters.REQUEST:
    case filtersActions.toggleBankTransactionsDateFilter.REQUEST:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          cursor: null,
          loading: true,
        },
      }

    case actions.fetchBankTransactions.SUCCESS:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          data: action.payload.results,
          error: null,
          fetched: true,
          loading: false,
          nextPageUrl: action.payload.next,
          prevPageUrl: action.payload.previous,
        },
      }

    case actions.fetchBankTransactions.FAILURE:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          error: action.payload,
          loading: false,
        },
      }

    // pagination
    case actions.fetchBankTransactionsByPaging.REQUEST: {
      const cursor = getCursorFromUrl(action.payload.url)
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          cursor,
          fetched: false,
          loading: true,
        },
      }
    }

    case actions.updateBankTransactionsRowsPerPage.REQUEST:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          cursor: null,
          loading: true,
          pageSize: action.payload.pageSize,
        },
      }

    case actions.resetBankTransactionsPagination.REQUEST:
      return {
        ...state,
        bankTransactions: {
          ...state.bankTransactions,
          cursor: null,
        },
      }

    //* PAYMENT TRANSACTIONS
    case filtersActions.initPaymentTransactionListParamsFromUrl.REQUEST: {
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          ...action.payload,
        },
      }
    }

    case actions.fetchPaymentTransactions.REQUEST:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          fetched: false,
          loading: true,
        },
      }

    case filtersActions.updatePaymentTransactionsFilters.REQUEST:
    case filtersActions.resetPaymentTransactionsFilters.REQUEST:
    case filtersActions.togglePaymentTransactionsDateFilter.REQUEST:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          cursor: null,
          loading: true,
        },
      }

    case actions.fetchPaymentTransactions.SUCCESS:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          data: action.payload.results,
          error: null,
          fetched: true,
          loading: false,
          nextPageUrl: action.payload.next,
          prevPageUrl: action.payload.previous,
        },
      }

    case actions.fetchPaymentTransactions.FAILURE:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          error: action.payload,
          loading: false,
        },
      }

    // pagination
    case actions.fetchPaymentTransactionsByPaging.REQUEST: {
      const cursor = getCursorFromUrl(action.payload.url)
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          cursor,
          fetched: false,
          loading: true,
        },
      }
    }

    case actions.updatePaymentTransactionsRowsPerPage.REQUEST:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          cursor: null,
          loading: true,
          pageSize: action.payload.pageSize,
        },
      }

    case actions.resetPaymentTransactionsPagination.REQUEST:
      return {
        ...state,
        paymentTransactions: {
          ...state.paymentTransactions,
          cursor: null,
        },
      }

    default:
      return state
  }
}

export default reducer
